import { useState } from 'react'
import { Link } from 'react-router-dom'
import { buildReservationPath } from '@/lib/reservation-url'
import {
  IconPlus,
  IconSearch,
  IconCalendar,
  IconCheck,
  IconX,
  IconClock,
  IconLogin,
  IconLogout,
  IconUser,
  IconMail,
  IconUsers,
  IconBed,
  IconPencil,
  IconAlertCircle,
  IconStar,
  IconCurrencyEuro,
} from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { ReservationsService, type Reservation } from '@/services/reservations'
import { EditReservaModal } from '@/components/reserves/EditReservaModal'
import { AdvancedFilters, type AdvancedFiltersState } from '@/components/reserves/AdvancedFilters'
import { QUICK_VIEWS } from '@/lib/constants/quick-views'
import { useReservations } from '@/hooks/useReservations'
import { cancelResultToast, type CancelResult } from '@/lib/cancel-result-toast'

export default function ReservesPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>({})
  const [selectedQuickView, setSelectedQuickView] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null)
  const { success, error, warning, info } = useToast()

  // TanStack Query Hook
  const {
    data: response,
    isLoading: loading,
    refetch
  } = useReservations({
    page: currentPage,
    searchTerm,
    statusFilter,
    advancedFilters
  })

  const reservations = response?.data || []
  const totalPages = response?.meta.total_pages || 1
  const totalItems = response?.meta.total || 0

  const handleAction = async (action: string, id: string) => {
    if (!window.confirm(`Estàs segur que vols ${action} aquesta reserva?`)) {
      return
    }

    try {
      switch (action) {
        case 'confirmar':
          await ReservationsService.confirmar(id)
          success('Reserva confirmada correctament')
          break
        case 'checkin':
          await ReservationsService.checkin(id)
          success('Check-in realitzat correctament')
          break
        case 'checkout':
          await ReservationsService.checkout(id)
          success('Check-out realitzat correctament')
          break
        case 'cancel·lar': {
          // El backend diu què ha passat de debò (Beds24 encuat o fallat, panys): s'ensenya el
          // seu `message` i no un «correctament» fix (ARQ-2).
          const result = (await ReservationsService.cancel(id)) as unknown as CancelResult
          const toast = cancelResultToast(result)
          if (toast.kind === 'warning') warning(toast.title, toast.description)
          else if (toast.kind === 'info') info(toast.title, toast.description)
          else success(toast.title, toast.description)
          break
        }
      }
      refetch()
    } catch (err: any) {
      error(err.message || `Error en ${action}`)
      console.error(err)
    }
  }

  const handleToggleContactat = async (id: string) => {
    try {
      await ReservationsService.toggleContactat(id)
      success('Estat de contacte actualitzat')
      refetch()
    } catch (err: any) {
      error('Error en actualitzar estat de contacte')
      console.error(err)
    }
  }

  const handleTogglePrioritat = async (id: string) => {
    try {
      await ReservationsService.togglePrioritat(id)
      success('Prioritat actualitzada')
      refetch()
    } catch (err: any) {
      error('Error en actualitzar prioritat')
      console.error(err)
    }
  }

  const applyQuickView = (viewId: string) => {
    if (viewId === 'all') {
      setAdvancedFilters({})
      setSelectedQuickView('all')
    } else {
      const view = QUICK_VIEWS.find(v => v.id === viewId)
      if (view) {
        setAdvancedFilters(view.filters)
        setSelectedQuickView(viewId)
      }
    }
  }

  const clearQuickView = () => {
    setAdvancedFilters({})
    setSelectedQuickView('all')
  }

  const getStatusBadge = (estat: string) => {
    const statusConfig: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', text: string, color: string }> = {
      PENDING: { variant: 'secondary', text: 'Pendent', color: 'bg-yellow-100 text-yellow-800' },
      CONFIRMED: { variant: 'default', text: 'Confirmada', color: 'bg-green-100 text-green-800' },
      CHECKED_IN: { variant: 'default', text: 'Check-in', color: 'bg-blue-100 text-blue-800' },
      CHECKED_OUT: { variant: 'outline', text: 'Check-out', color: 'bg-gray-100 text-gray-800' },
      CANCELLED: { variant: 'destructive', text: 'Cancel·lada', color: 'bg-red-100 text-red-800' },
    }

    const config = statusConfig[estat] || { variant: 'secondary', text: estat, color: 'bg-gray-100 text-gray-800' }

    return (
      <Badge variant={config.variant} className={config.color}>
        {config.text}
      </Badge>
    )
  }

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return '-'
    return new Intl.NumberFormat('ca-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'

    // Si la fecha viene como "2025-01-15" (formato ISO sin hora)
    // agregamos la hora para evitar problemas de timezone
    const date = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T00:00:00')

    if (isNaN(date.getTime())) return 'Data invàlida'

    return date.toLocaleDateString('ca-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-purple-200 to-blue-200 rounded-full blur-3xl opacity-15 animate-pulse delay-300"></div>
      </div>

      <div className="relative flex flex-col gap-8 py-8 px-6 md:px-8">
        {/* Header */}
        <div className="animate-in fade-in-2 slide-in-from-top-4 duration-1000">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Reserves
              </h1>
              <p className="text-lg text-gray-600">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Gestiona les teves reserves
                </span>
              </p>
            </div>
            {/* F4 (runbook 7): les reserves es creen des d'una cel·la buida del calendari (v0.53.0). */}
            <Button
              asChild
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl h-12 px-6 w-full sm:w-auto"
            >
              <Link to="/calendar-v2">
                <IconPlus className="mr-2 h-5 w-5" />
                Nova reserva → calendari
              </Link>
            </Button>
          </div>
        </div>

        {/* Filtres i cerca */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-200">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-0 sm:space-x-4">
                <div className="relative w-full sm:flex-1 sm:max-w-sm group">
                  <IconSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-colors duration-300 group-focus-within:text-blue-600" />
                  <Input
                    placeholder="Cercar per nom, email, telèfon o ID Beds24..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl h-12 transition-all duration-300"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl h-12 transition-all duration-300">
                    <SelectValue placeholder="Estat" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-gray-200 rounded-xl shadow-xl">
                    <SelectItem value="all" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">Totes</SelectItem>
                    <SelectItem value="PENDING" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">Pendents</SelectItem>
                    <SelectItem value="CONFIRMED" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">Confirmades</SelectItem>
                    <SelectItem value="CHECKED_IN" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">Check-in</SelectItem>
                    <SelectItem value="CHECKED_OUT" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">Check-out</SelectItem>
                    <SelectItem value="CANCELLED" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">Cancel·lades</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Vistas Rápidas */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-300">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Vistes Ràpides:</span>
              </div>
              <Select value={selectedQuickView} onValueChange={applyQuickView}>
                <SelectTrigger className="w-[240px] border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl h-11 transition-all duration-300">
                  <SelectValue placeholder="Selecciona una vista..." />
                </SelectTrigger>
                <SelectContent className="bg-white/95 backdrop-blur-xl border-gray-200 rounded-xl shadow-xl">
                  <SelectItem value="all" className="rounded-lg hover:bg-blue-50 transition-colors duration-200">
                    <span className="flex items-center gap-2">
                      <span>📋</span>
                      <span>Totes les reserves</span>
                    </span>
                  </SelectItem>
                  {QUICK_VIEWS.map((view) => (
                    <SelectItem
                      key={view.id}
                      value={view.id}
                      className="rounded-lg hover:bg-blue-50 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <span>{view.icon}</span>
                        <div className="flex flex-col">
                          <span className="font-medium">{view.name}</span>
                          <span className="text-xs text-gray-500">{view.description}</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedQuickView !== 'all' && (
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors duration-200 px-3 py-1.5"
                >
                  <span className="flex items-center gap-1.5">
                    <span>{QUICK_VIEWS.find(v => v.id === selectedQuickView)?.icon}</span>
                    <span className="font-medium">
                      Vista activa: {QUICK_VIEWS.find(v => v.id === selectedQuickView)?.name}
                    </span>
                    <button
                      onClick={clearQuickView}
                      className="ml-1 hover:text-blue-900 transition-colors"
                    >
                      <IconX className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Filtros Avanzados */}
        <div>
          <AdvancedFilters
            filters={advancedFilters}
            onFilterChange={(newFilters) => {
              setAdvancedFilters(newFilters)
              setSelectedQuickView('all') // Limpiar vista rápida si se modifican filtros manualmente
            }}
            onClear={() => {
              setAdvancedFilters({})
              setSelectedQuickView('all')
            }}
          />
        </div>

        {/* Estadístiques */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-400">
          <div className="grid gap-6 md:grid-cols-4">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{totalItems}</div>
                  <p className="text-gray-600 font-medium">Total Reserves</p>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-2xl">
                  <IconCalendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-600">
                    {reservations.filter(r => r.estat === 'CONFIRMED').length}
                  </div>
                  <p className="text-gray-600 font-medium">Confirmades</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-3 rounded-2xl">
                  <IconCheck className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {reservations.filter(r => r.estat === 'CHECKED_IN').length}
                  </div>
                  <p className="text-gray-600 font-medium">Check-in</p>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-2xl">
                  <IconLogin className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-yellow-600">
                    {reservations.filter(r => r.estat === 'PENDING').length}
                  </div>
                  <p className="text-gray-600 font-medium">Pendents</p>
                </div>
                <div className="bg-gradient-to-r from-yellow-500 to-orange-600 p-3 rounded-2xl">
                  <IconClock className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Taula */}
        <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-600">
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-blue-50/50">
                  <TableHead className="font-semibold text-gray-700">Hoste</TableHead>
                  <TableHead className="font-semibold text-gray-700">Dates</TableHead>
                  <TableHead className="font-semibold text-gray-700">Habitació</TableHead>
                  <TableHead className="font-semibold text-gray-700">Ocupació</TableHead>
                  <TableHead className="font-semibold text-gray-700">Preu</TableHead>
                  <TableHead className="font-semibold text-gray-700">Estat</TableHead>
                  <TableHead className="font-semibold text-gray-700">Etiquetes</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Accions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-gray-600">Carregant reserves...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : reservations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="text-gray-500">No s'han trobat reserves</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  reservations.map((reservation) => (
                    <TableRow key={reservation.id} className="border-gray-200 hover:bg-blue-50/30 transition-all duration-200">
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-3">
                          <div className={`${reservation.client ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-blue-500 to-purple-600'} p-2 rounded-xl`}>
                            <IconUser className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {/* 🆕 F1 — El nom porta a l'adreça de la reserva */}
                              <Link
                                to={buildReservationPath([reservation.beds24_id])}
                                className="hover:underline hover:text-blue-700"
                                title={`Obrir la fitxa #${reservation.beds24_id}`}
                              >
                                {reservation.guest_name}
                              </Link>
                              {reservation.client && (
                                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                  Client Registrat
                                </span>
                              )}
                            </div>
                            {reservation.guest_email && (
                              <div className="text-sm text-gray-500 flex items-center gap-1">
                                <IconMail className="h-3 w-3" />
                                {reservation.guest_email}
                              </div>
                            )}
                            {reservation.client && (
                              <div className="text-xs text-green-600 font-medium mt-0.5">
                                {reservation.client.nom} {reservation.client.cognoms}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <IconLogin className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium">{formatDate(reservation.data_entrada)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <IconLogout className="h-4 w-4 text-purple-600" />
                            <span className="text-sm font-medium">{formatDate(reservation.data_sortida)}</span>
                          </div>
                          <div className="text-xs text-gray-500">{reservation.nits} nits</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <IconBed className="h-4 w-4 text-blue-600" />
                          <div className="text-sm">
                            <div className="font-medium text-gray-700">{reservation.room?.name || '-'}</div>
                            <div className="text-gray-500">{reservation.room?.type || ''}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <IconUsers className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium">{reservation.num_adults + reservation.num_nens} persones</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {reservation.num_adults} adults, {reservation.num_nens} nens
                        </div>
                        {reservation.guests && reservation.guests.length > 0 && (
                          <div className="text-xs text-green-600 font-medium mt-1">
                            ✓ {reservation.guests.length} {reservation.guests.length === 1 ? 'viatger' : 'viatgers'} registrat{reservation.guests.length === 1 ? '' : 's'}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-blue-600">{formatCurrency(reservation.preu_total)}</div>
                      </TableCell>
                      <TableCell>{getStatusBadge(reservation.estat)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {/* Badge Estado de Pago */}
                          {reservation.metadata?.estat_pagament === 'PENDENT' && (
                            <Badge variant="destructive" className="text-xs">
                              <IconAlertCircle className="h-3 w-3 mr-1" />
                              Pendent Pagament
                            </Badge>
                          )}
                          {reservation.metadata?.estat_pagament === 'PARCIAL' && (
                            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                              <IconCurrencyEuro className="h-3 w-3 mr-1" />
                              Parcial
                            </Badge>
                          )}
                          {reservation.metadata?.estat_pagament === 'PAGAT' && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                              <IconCheck className="h-3 w-3 mr-1" />
                              Pagat
                            </Badge>
                          )}

                          {/* Badge No Contactado */}
                          {!reservation.metadata?.contactat && (
                            <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300">
                              <IconMail className="h-3 w-3 mr-1" />
                              Sense contactar
                            </Badge>
                          )}

                          {/* Badge Prioridad */}
                          {reservation.metadata?.prioritat && (
                            <Badge className="text-xs bg-amber-500 hover:bg-amber-600">
                              <IconStar className="h-3 w-3 mr-1" />
                              Prioritària
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {/* Botón Editar (siempre visible) */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedReservation(reservation)
                              setShowEditModal(true)
                            }}
                            className="hover:bg-blue-50 hover:border-blue-300 rounded-lg"
                            title="Editar reserva"
                          >
                            <IconPencil className="h-4 w-4 text-blue-600" />
                          </Button>

                          {/* Acciones rápidas metadata */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleContactat(reservation.id)}
                            className={`hover:bg-orange-50 hover:border-orange-300 rounded-lg ${reservation.metadata?.contactat ? 'bg-green-50 border-green-300' : ''
                              }`}
                            title={reservation.metadata?.contactat ? 'Marcar com no contactat' : 'Marcar com contactat'}
                          >
                            <IconMail className={`h-4 w-4 ${reservation.metadata?.contactat ? 'text-green-600' : 'text-orange-600'}`} />
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTogglePrioritat(reservation.id)}
                            className={`hover:bg-amber-50 hover:border-amber-300 rounded-lg ${reservation.metadata?.prioritat ? 'bg-amber-100 border-amber-400' : ''
                              }`}
                            title={reservation.metadata?.prioritat ? 'Treure prioritat' : 'Marcar com prioritària'}
                          >
                            <IconStar className={`h-4 w-4 ${reservation.metadata?.prioritat ? 'text-amber-600' : 'text-gray-400'}`} />
                          </Button>

                          {/* Acciones rápidas según estado */}
                          {reservation.estat === 'PENDING' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('confirmar', reservation.id)}
                              className="hover:bg-green-50 hover:border-green-300 rounded-lg"
                            >
                              <IconCheck className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {reservation.estat === 'CONFIRMED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('checkin', reservation.id)}
                              className="hover:bg-blue-50 hover:border-blue-300 rounded-lg"
                            >
                              <IconLogin className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          {reservation.estat === 'CHECKED_IN' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('checkout', reservation.id)}
                              className="hover:bg-purple-50 hover:border-purple-300 rounded-lg"
                            >
                              <IconLogout className="h-4 w-4 text-purple-600" />
                            </Button>
                          )}
                          {['PENDING', 'CONFIRMED'].includes(reservation.estat) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction('cancel·lar', reservation.id)}
                              className="hover:bg-red-50 hover:border-red-300 rounded-lg"
                            >
                              <IconX className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Paginació */}
        {totalPages > 1 && (
          <div className="animate-in fade-in-2 slide-in-from-bottom-4 duration-1000 delay-800">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-4 sm:p-6 shadow-xl">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600 sm:text-base">
                  Mostrant {((currentPage - 1) * 20) + 1} a {Math.min(currentPage * 20, totalItems)} de {totalItems} reserves
                </p>
                <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-0 sm:space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="border-gray-200 hover:bg-blue-50 hover:border-blue-300 rounded-xl px-3 sm:px-4 py-2 transition-all duration-300 disabled:opacity-50"
                  >
                    Anterior
                  </Button>
                  <span className="whitespace-nowrap text-sm sm:text-base text-gray-700 font-medium px-2 sm:px-3 py-2 bg-blue-50 rounded-xl border border-blue-200">
                    Pàgina {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="border-gray-200 hover:bg-blue-50 hover:border-blue-300 rounded-xl px-3 sm:px-4 py-2 transition-all duration-300 disabled:opacity-50"
                  >
                    Següent
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Editar Reserva */}
        {selectedReservation && (
          <EditReservaModal
            open={showEditModal}
            onOpenChange={setShowEditModal}
            onSuccess={() => refetch()}
            reserva={selectedReservation}
          />
        )}

      </div>
    </div>
  )
}
